import React, { useState, useRef, useEffect, useMemo } from "react";
import { cn } from "../../../lib/utils";
import { capitalizeAll } from "../../../utils/common/stringManipulation";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Button } from "../../ui/button";
import { CalendarIcon, Check, ChevronsUpDown, Info, Loader2, Lock, Plus, Search } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { FieldWidth } from "./fieldLayoutConfig";
import { parseFormDate } from "../../../utils/vault/parseFormDate";

// Label component with our design system styling
interface FormLabelProps {
  children: React.ReactNode;
  required?: boolean;
  tooltip?: string;
  className?: string;
  htmlFor?: string;
}

export const FormLabel: React.FC<FormLabelProps> = ({
  children,
  required,
  tooltip,
  className,
  htmlFor,
}) => (
  <label
    htmlFor={htmlFor}
    className={cn(
      "block text-sm font-medium text-gray-700 mb-2",
      className
    )}
  >
    {required && <span className="text-red-500 mr-1">*</span>}
    {children}
    {tooltip && (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="inline-block h-3.5 w-3.5 ml-1.5 text-gray-400 cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
  </label>
);

// Error message component
interface FormErrorProps {
  message?: string;
}

export const FormError: React.FC<FormErrorProps> = ({ message }) => {
  if (!message) return null;
  return <p className="mt-1.5 text-sm text-red-500">{message}</p>;
};

// Form field wrapper with consistent spacing
interface FormFieldWrapperProps {
  children: React.ReactNode;
  className?: string;
  width?: FieldWidth;
  hidden?: boolean;
  fieldKey?: string;
}

export const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  children,
  className,
  width = "full",
  hidden = false,
  fieldKey,
}) => {
  if (hidden) return null;

  return (
    <div
      data-field-key={fieldKey}
      className={cn(
        "mb-5",
        width === "third"
          ? "w-full md:w-1/3 md:px-2"
          : width === "half"
            ? "w-full md:w-1/2 md:px-2"
            : "w-full md:px-2",
        className
      )}
    >
      {children}
    </div>
  );
};

// Helper to apply case style transformation to a string value
const applyCaseStyle = (value: string, caseStyle?: string): string => {
  if (!caseStyle || !value) return value;
  switch (caseStyle) {
    case "uppercase":
      return value.toUpperCase();
    case "lowercase":
      return value.toLowerCase();
    case "capitalize":
      return capitalizeAll(value);
    default:
      return value;
  }
};

// Text Input Field
interface TextFieldProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  type?: "text" | "email" | "number" | "tel";
  maxLength?: number;
  /** Case transformation style: "uppercase", "lowercase", or "capitalize" */
  caseStyle?: string;
  className?: string;
  /** Indicates the field is locked (pre-filled from verification) */
  locked?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  /** Async validation in flight (e.g. email DNS check) — shows a hint */
  validating?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  value = "",
  onChange,
  onBlur,
  placeholder,
  required,
  disabled,
  error,
  tooltip,
  type = "text",
  maxLength,
  caseStyle,
  className,
  locked,
  inputMode,
  autoComplete,
  validating,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Filter non-digit characters for numeric-only fields (postal code, phone, etc.)
    if (inputMode === "numeric" || inputMode === "tel") {
      val = val.replace(/\D/g, "");
    }
    onChange(applyCaseStyle(val, caseStyle));
  };

  return (
    <div className={className}>
      <FormLabel htmlFor={name} required={required} tooltip={tooltip}>
        {label}
      </FormLabel>
      <div className="relative">
        <Input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          aria-required={required}
          maxLength={maxLength}
          inputMode={inputMode}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          className={cn(
            error && "border-red-500 focus:border-red-500",
            locked && "bg-gray-50 border-gray-200 pr-10"
          )}
          style={
            caseStyle === "uppercase" || caseStyle === "lowercase"
              ? { textTransform: caseStyle as "uppercase" | "lowercase" }
              : caseStyle === "capitalize"
                ? { textTransform: "capitalize" }
                : undefined
          }
        />
        {locked && (
          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        )}
        {maxLength && (
          <p className="absolute right-2 bottom-2 text-xs text-gray-400 pointer-events-none">
            {value?.length || 0} / {maxLength}
          </p>
        )}
      </div>
      {validating && !error && (
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-400" aria-live="polite">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Verificando correo…
        </p>
      )}
      <FormError message={error} />
    </div>
  );
};

// Currency Input Field
interface CurrencyFieldProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  className?: string;
  locked?: boolean;
}

export const CurrencyField: React.FC<CurrencyFieldProps> = ({
  name,
  label,
  value = "",
  onChange,
  onBlur,
  placeholder,
  required,
  disabled,
  error,
  tooltip,
  className,
  locked,
}) => {
  // rent_amount / maintenance_amount accept up to 2 decimals; every other currency field
  // stays integer-only (matches the backend form_validation rules).
  const allowsDecimals =
    name === "rent_amount" ||
    name === "maintenance_amount" ||
    name === "deposit_amount";

  const cleanDecimal = (raw: string) => {
    let cleaned = raw.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      const intPart = cleaned.slice(0, firstDot);
      const decPart = cleaned
        .slice(firstDot + 1)
        .replace(/\./g, "")
        .slice(0, 2);
      cleaned = `${intPart}.${decPart}`;
    }
    return cleaned;
  };

  const formatCurrency = (val: string) => {
    if (!allowsDecimals) {
      const numbers = val.replace(/[^0-9]/g, "");
      if (!numbers) return "";
      return new Intl.NumberFormat("es-MX").format(parseInt(numbers));
    }
    const cleaned = cleanDecimal(val);
    if (!cleaned) return "";
    const dot = cleaned.indexOf(".");
    if (dot === -1)
      return new Intl.NumberFormat("es-MX").format(parseInt(cleaned));
    const intPart = cleaned.slice(0, dot);
    const decPart = cleaned.slice(dot + 1);
    const intNum = intPart === "" ? 0 : parseInt(intPart);
    return `${new Intl.NumberFormat("es-MX").format(intNum)}.${decPart}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = allowsDecimals
      ? cleanDecimal(e.target.value)
      : e.target.value.replace(/[^0-9]/g, "");
    onChange(rawValue);
  };

  return (
    <div className={className}>
      <FormLabel required={required} tooltip={tooltip}>
        {label}
      </FormLabel>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          $
        </span>
        <Input
          id={name}
          name={name}
          type="text"
          value={formatCurrency(value)}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={allowsDecimals ? "decimal" : "numeric"}
          aria-invalid={!!error}
          className={cn(
            "pl-7",
            locked ? "pr-20 bg-gray-50 border-gray-200" : "pr-14",
            error && "border-red-500 focus:border-red-500"
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          <span className="text-gray-500 text-sm">MXN</span>
          {locked && <Lock className="h-4 w-4 text-gray-400" />}
        </div>
      </div>
      <FormError message={error} />
    </div>
  );
};

// Normalize text for accent-insensitive search
const normalizeText = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Select Field
interface SelectFieldProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: string[] | { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  searchable?: boolean;
  allowAddItem?: boolean;
  className?: string;
  locked?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  placeholder = "Seleccionar...",
  required,
  disabled,
  error,
  tooltip,
  searchable = false,
  allowAddItem = false,
  className,
  locked,
}) => {
  const normalizedOptions = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "string" ? { label: opt, value: opt } : opt
      ),
    [options]
  );

  // For non-searchable selects, use the standard Radix Select
  if (!searchable) {
    return (
      <div className={className}>
        <FormLabel htmlFor={name} required={required} tooltip={tooltip}>
          {label}
        </FormLabel>
        <div className="relative">
          <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
              id={name}
              onBlur={onBlur}
              aria-required={required}
              className={cn(
                "h-11 text-base md:text-sm",
                error && "border-red-500 focus:border-red-500",
                locked && "bg-gray-50 border-gray-200 pr-10"
              )}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {normalizedOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {locked && (
            <Lock className="absolute right-8 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          )}
        </div>
        <FormError message={error} />
      </div>
    );
  }

  // Searchable combobox using Popover
  return (
    <SearchableSelect
      name={name}
      label={label}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      options={normalizedOptions}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={error}
      tooltip={tooltip}
      allowAddItem={allowAddItem}
      className={className}
      locked={locked}
    />
  );
};

// Searchable Select (Combobox) built with Popover + Input + filtered list
interface SearchableSelectProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  allowAddItem?: boolean;
  className?: string;
  locked?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  placeholder = "Seleccionar...",
  required,
  disabled,
  error,
  tooltip,
  allowAddItem = false,
  className,
  locked,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addingItem, setAddingItem] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const effectiveOptions = useMemo(() => {
    const currentValue = value;
    const currentLabel = currentValue?.trim();
    if (
      !currentValue ||
      !currentLabel ||
      options.some((opt) => opt.value === currentValue)
    ) {
      return options;
    }

    return [...options, { label: currentLabel, value: currentValue }];
  }, [options, value]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [open]);

  const selectedLabel = useMemo(() => {
    const found = effectiveOptions.find((opt) => opt.value === value);
    return found?.label;
  }, [effectiveOptions, value]);

  const filteredOptions = useMemo(() => {
    if (!search) return effectiveOptions;
    const normalizedSearch = normalizeText(search);
    return effectiveOptions.filter((opt) =>
      normalizeText(opt.label).includes(normalizedSearch)
    );
  }, [effectiveOptions, search]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setOpen(false);
    setSearch("");
  };

  const handleAddItem = () => {
    const trimmed = addingItem.trim();
    if (!trimmed) return;
    if (options.some((opt) => opt.value === trimmed)) {
      // Already exists, just select it
      handleSelect(trimmed);
    } else {
      onChange(trimmed);
      setOpen(false);
    }
    setAddingItem("");
  };

  return (
    <div className={className}>
      <FormLabel htmlFor={name} required={required} tooltip={tooltip}>
        {label}
      </FormLabel>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              id={name}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-required={required}
              onBlur={onBlur}
              disabled={disabled}
              className={cn(
                "h-11 w-full justify-between text-left font-normal border-gray-200 shadow-none text-base md:text-sm",
                "hover:bg-transparent hover:border-gray-300",
                !value && "text-gray-400",
                error && "border-red-500 focus:border-red-500",
                locked && "bg-gray-50 border-gray-200 pr-10"
              )}
            >
              <span className="truncate">
                {selectedLabel || placeholder}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
          >
            {/* Search input */}
            <div className="flex items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              className="max-h-60 overflow-y-auto p-1"
            >
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  Sin resultados
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
                      "hover:bg-accent hover:text-accent-foreground",
                      option.value === value && "bg-accent/50"
                    )}
                  >
                    {option.value === value && (
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                    {option.label}
                  </button>
                ))
              )}
            </div>

            {/* Add custom item section */}
            {allowAddItem && (
              <div className="border-t p-2">
                <div className="flex items-center gap-2">
                  <input
                    value={addingItem}
                    onChange={(e) => setAddingItem(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddItem();
                      }
                    }}
                    placeholder="Agregar opcion..."
                    className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddItem}
                    className="shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {locked && (
          <Lock className="absolute right-8 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        )}
      </div>
      <FormError message={error} />
    </div>
  );
};

// Date Field
interface DateFieldProps {
  name: string;
  label: string;
  value?: string | Date;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  className?: string;
  locked?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  placeholder = "dd/mm/aaaa",
  required,
  disabled,
  error,
  tooltip,
  className,
  locked,
}) => {
  const [open, setOpen] = React.useState(false);

  const dateValue = React.useMemo(() => parseFormDate(value), [value]);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
    }
    setOpen(false);
    onBlur?.();
  };

  return (
    <div className={className}>
      <FormLabel required={required} tooltip={tooltip}>
        {label}
      </FormLabel>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={name}
              variant="outline"
              disabled={disabled}
              className={cn(
                "h-11 w-full justify-start text-left font-normal border-gray-200 shadow-none",
                "hover:bg-transparent hover:border-gray-300",
                "focus:border-gray-900",
                !dateValue && "text-gray-400",
                error && "border-red-500 focus:border-red-500",
                locked && "bg-gray-50 border-gray-200 pr-10"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
              {dateValue ? (
                format(dateValue, "dd/MM/yyyy", { locale: es })
              ) : (
                <span>{placeholder}</span>
              )}
            </Button>
          </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
              mode="single"
              selected={dateValue}
              defaultMonth={dateValue}
              onSelect={handleSelect}
              captionLayout="dropdown"
              startMonth={new Date(1900, 0)}
              endMonth={new Date(new Date().getFullYear() + 10, 11)}
              locale={es}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {locked && (
          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        )}
      </div>
      <FormError message={error} />
    </div>
  );
};

// Radio Field
interface RadioFieldProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: string[] | { label: string; value: string }[];
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  direction?: "horizontal" | "vertical";
  className?: string;
  /** Indicates the field is locked (pre-filled from verification) */
  locked?: boolean;
}

export const RadioField: React.FC<RadioFieldProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  required,
  disabled,
  error,
  tooltip,
  direction = "horizontal",
  className,
  locked,
}) => {
  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <FormLabel required={required} tooltip={tooltip}>
          {label}
        </FormLabel>
        {locked && <Lock className="h-4 w-4 text-gray-400" />}
      </div>
      <div className={cn(locked && "bg-gray-50 rounded-md p-2 border border-gray-200")}>
        <RadioGroup
          value={value || ""}
          onValueChange={onChange}
          disabled={disabled}
          className={cn(
            direction === "horizontal" ? "flex flex-wrap gap-4" : "space-y-2"
          )}
        >
          {normalizedOptions.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem
                value={option.value}
                id={`${name}-${option.value}`}
                className="border-gray-300 text-gray-900"
              />
              <label
                htmlFor={`${name}-${option.value}`}
                className="text-sm text-gray-700 cursor-pointer"
              >
                {option.label}
              </label>
            </div>
          ))}
        </RadioGroup>
      </div>
      <FormError message={error} />
    </div>
  );
};

// Checkbox Field
interface CheckboxFieldProps {
  name: string;
  label: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Indicates the field is locked (pre-filled from verification) */
  locked?: boolean;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  name,
  label,
  checked = false,
  onChange,
  required,
  disabled,
  error,
  className,
  locked,
}) => (
  <div className={cn(className, locked && "bg-gray-50 rounded-md p-2 border border-gray-200")}>
    <div className="flex items-start space-x-2">
      <Checkbox
        id={name}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
      <label
        htmlFor={name}
        className="text-sm text-gray-700 cursor-pointer leading-tight flex items-center gap-2"
      >
        {required && <span className="text-red-500 mr-1">*</span>}
        {label}
        {locked && <Lock className="h-3.5 w-3.5 text-gray-400" />}
      </label>
    </div>
    <FormError message={error} />
  </div>
);

// Textarea Field
interface TextareaFieldProps {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  tooltip?: string;
  maxLength?: number;
  minLength?: number;
  rows?: number;
  className?: string;
  locked?: boolean;
}

export const TextareaField: React.FC<TextareaFieldProps> = ({
  name,
  label,
  value = "",
  onChange,
  onBlur,
  placeholder,
  required,
  disabled,
  error,
  tooltip,
  maxLength = 500,
  minLength,
  rows = 4,
  className,
  locked,
}) => {
  const effectiveMinLength = required ? (minLength ?? 10) : minLength;

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <FormLabel required={required} tooltip={tooltip}>
          {label}
        </FormLabel>
        {locked && <Lock className="h-4 w-4 text-gray-400" />}
      </div>
      <Textarea
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={rows}
        aria-invalid={!!error}
        className={cn(
          "resize-none",
          error && "border-red-500 focus:border-red-500 focus:ring-red-500",
          locked && "bg-gray-50 border-gray-200"
        )}
      />
      <div className="flex justify-between mt-1">
        <div className="flex items-center gap-2">
          <FormError message={error} />
          {effectiveMinLength && !error && value.length > 0 && value.length < effectiveMinLength && (
            <p className="text-xs text-amber-500">
              Min. {effectiveMinLength} caracteres
            </p>
          )}
        </div>
        <p className="text-xs text-gray-400">
          {value?.length || 0} / {maxLength}
        </p>
      </div>
    </div>
  );
};

// Section Header
interface FormSectionProps {
  title: string;
  className?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({ title, className }) => (
  <div className={cn("w-full md:px-2 pt-8 first:pt-0", className)}>
    <div className="border-b border-slate-200 pb-3 mb-6">
      <h3 className="text-base font-bold text-slate-800 uppercase tracking-wide">
        {title}
      </h3>
    </div>
  </div>
);
